import { monthName } from '../lib/i18n/format'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Chip, Meter } from '@heroui/react'
import { api } from '../lib/api'
import { accepts } from '../lib/filePicker'
import { explain } from '../lib/staffErrors'
import { formatMoment, hasPhrase, type StaffKey, type StaffLanguage } from '../lib/staffText'
import type { LicenceState, LicenceStatus } from '../lib/types'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { METER_COLOR, meterState, meterValue } from './licenceUsage'
import { useFlash } from './sectionKit'
import { Notice, PageHead, SettingRow, SettingsCard, Values } from './settingsKit'
import '../styles/orgset.css'
import '../styles/install.css'

/**
 * The licence.
 *
 * This screen exists for two moments. The first is ordinary: somebody checking what
 * they bought against what they are using, once a quarter. The second is the one
 * worth designing for — something has gone wrong, configuration is refusing to
 * save, and the person looking at this screen needs to know in one sentence why and
 * what to do. So the state is stated in plain words before any of the figures, and
 * the installation code is right there to be copied into an email.
 *
 * What it never says is that the queue has stopped, because the queue has not
 * stopped. Every state below leaves customers being served.
 */

type Tone = 'ok' | 'warn'
// What the installation owner is told: in English, as the owner's screens are, and
// with the action that only the owner can take. An administrator reading the licence
// is told the state in their own language and that the owner can fix it.
const STATES: Record<LicenceState, { tone: Tone; says: string }> = {
  valid: { tone: 'ok', says: '' },
  unlicensed: { tone: 'warn', says: 'Everything works. Send us the request for a licence.' },
  // Nine times in ten a licence from the future is a server clock in the past.
  not_yet_valid: { tone: 'warn', says: 'Setup is locked. Check the server’s clock first.' },
  expired: { tone: 'warn', says: 'Setup is locked; customers are still served. Apply the renewal.' },
  foreign: { tone: 'warn', says: 'Setup is locked; customers are still served. Send us the code.' },
  needs_reactivation: { tone: 'warn', says: 'Setup is locked; customers are still served. Send us the request.' },
  // The licence is genuine: asking for a new file would produce the same one.
  needs_newer_server: { tone: 'warn', says: 'Setup is locked until this server is upgraded. The licence is fine.' },
  trial: { tone: 'ok', says: 'Everything works. Send us the request for a licence.' },
  trial_ended: { tone: 'warn', says: 'Setup is locked; customers are still served. Apply a licence.' },
  revoked: { tone: 'warn', says: 'Setup is locked; customers are still served. Contact Qmeter.' },
  clock_behind: { tone: 'warn', says: 'Setup is locked until the server clock is right.' },
}

/** A trial reads as one: no holder, no file, no right-to-use dates. */
export function isTrial(state: LicenceState): boolean {
  return state === 'trial' || state === 'trial_ended'
}

/** Saves a JSON document as a file, in the browser. */
function saveJson(value: unknown, filename: string) {
  const a = document.createElement('a')
  a.href = URL.createObjectURL(new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' }))
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.setTimeout(() => URL.revokeObjectURL(a.href), 2000)
}


/** 31 January 2027, for the owner's English lines. */
function longDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : `${d.getDate()} ${monthName('en', d.getMonth())} ${d.getFullYear()}`
}

/** A date with its year, in the reader's language. */
function dated(iso: string, lang: StaffLanguage): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : `${formatMoment(d, lang, { date: true })} ${d.getFullYear()}`
}

/** Whole days from today to the date; negative once it has passed. */
function daysFromToday(iso: string): number {
  const d = new Date(iso)
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  return Math.round((start(d) - start(new Date())) / 86400000)
}

function inDays(n: number, say: Say): string {
  if (n === 0) return say('admin.licence.today')
  return n > 0 ? say('admin.licence.in', { n }) : say('admin.licence.ago', { n: -n })
}

/** A module's name in the reader's language, falling back to the server's label. */
function moduleName(name: string, label: string, say: Say): string {
  for (const key of [`org.br.module.${name}`, `admin.licence.module.${name}`]) {
    if (hasPhrase(key)) return say(key as StaffKey)
  }
  return label
}

type Say = (key: StaffKey, vars?: Record<string, string | number>) => string

/**
 * manage is licence.manage: the installation owner, who applies files and sees the
 * installation code. Without it the screen is the licence as an organisation's
 * administrator reads it (licence.read) — what was bought and what is used.
 */
export function Licence({ manage = true }: { manage?: boolean }) {
  const { say } = useStaffText()
  const [status, setStatus] = useState<LicenceStatus | null>(null)
  const [loadError, setLoadError] = useState('')

  const load = useCallback(async () => {
    setStatus(await api.licence())
  }, [])

  useEffect(() => {
    load().catch(() => setLoadError('The licence could not be read.'))
  }, [load])

  if (!status)
    return (
      <>
        <PageHead title={say('admin.section.licence')} line={say('settings.licence.line')} />
        {loadError && <Notice>{loadError}</Notice>}
      </>
    )
  return <LicenceView status={status} manage={manage} onStatus={setStatus} reload={load} />
}

type Busy = '' | 'licence' | 'revocations' | 'request'

/** The page for a status already read; the tests draw it on its own. */
export function LicenceView({
  status,
  manage,
  onStatus,
  reload,
}: {
  status: LicenceStatus
  manage: boolean
  onStatus: (s: LicenceStatus) => void
  reload: () => Promise<void>
}) {
  const { lang, say } = useStaffText()
  const [fault, setFault] = useState('')
  const [busy, setBusy] = useState<Busy>('')
  const picker = useRef<HTMLInputElement>(null)
  const revPicker = useRef<HTMLInputElement>(null)
  const [copied, setCopied] = useState(false)
  const { flash, toast } = useFlash()

  async function run(what: Busy, work: () => Promise<void>) {
    setFault('')
    setBusy(what)
    try {
      await work()
    } catch (err) {
      setFault(explain(err, 'en', 'common.unreachable'))
    } finally {
      setBusy('')
    }
  }

  function apply(file: File) {
    // The dialog's "All files" ignores the filter; the server checks again anyway.
    if (!accepts('.qlic,application/json', file)) return setFault(`${file.name} is not a .qlic file.`)
    void run('licence', async () => {
      onStatus(await api.applyLicence(file))
      flash(say('settings.licence.applied'))
    })
  }

  function applyRevocations(file: File) {
    if (!accepts('.qrl,application/json', file)) return setFault(`${file.name} is not a .qrl file.`)
    void run('revocations', async () => {
      await api.applyRevocations(file)
      // The answer is the bare status; the page also needs its counts and modules.
      await reload()
      flash(say('settings.licence.revocationsApplied'))
    })
  }

  const downloadRequest = () =>
    void run('request', async () => {
      saveJson(await api.licenceRequest(), `qms-licence-request-${status.installId}.json`)
    })

  async function copyInstallId() {
    try {
      await navigator.clipboard.writeText(status.installId)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // Refused on an insecure origin or by policy; the code stays selectable on screen.
      setCopied(false)
    }
  }

  const state = STATES[status.state] ?? STATES.unlicensed
  const trial = isTrial(status.state)
  const label = say(`admin.licence.state.${status.state in STATES ? status.state : 'unlicensed'}` as StaffKey, {
    n: status.trialDaysLeft ?? 0,
  })
  // An edge shows its server's licence: nothing to apply, no code, no usage here.
  const own = manage && !status.fromServer && !status.plan
  const says = own
    ? state.says
    : state.tone === 'ok'
      ? ''
      : status.state === 'unlicensed'
        ? say('admin.licence.saysWorks')
        : say('admin.licence.saysLocked')
  const doc = status.licence

  const details: { label: string; value: string }[] = []
  if (doc && !trial) {
    details.push({
      label: say('settings.licence.holder'),
      value: status.plan ? doc.customer.name : say('admin.licence.who', { customer: doc.customer.name, id: doc.licenceId }),
    })
  }
  if (trial && status.trialEndsAt) details.push({ label: say('settings.licence.trialEnds'), value: dated(status.trialEndsAt, lang) })
  if (own && status.state === 'clock_behind' && status.seenAt) {
    const seen = new Date(status.seenAt)
    details.push({ label: say('settings.licence.seenAt'), value: `${longDate(status.seenAt)}, ${formatMoment(seen, lang, { time: true })}` })
  }

  const filePicker = (ref: typeof picker, accept: string, take: (f: File) => void) => (
    <input
      ref={ref}
      type="file"
      accept={accept}
      className="sr"
      tabIndex={-1}
      aria-hidden="true"
      disabled={busy !== ''}
      onChange={(e) => {
        const f = e.target.files?.[0]
        // Emptied whatever happens, so that choosing the same file again is a change.
        e.target.value = ''
        if (f) take(f)
      }}
    />
  )

  return (
    <>
      <PageHead
        title={say('admin.section.licence')}
        line={say(status.plan ? 'settings.licence.planLine' : 'settings.licence.line')}
        actions={
          own && (
            <>
              {filePicker(revPicker, '.qrl,application/json', applyRevocations)}
              <Button variant="secondary" isDisabled={busy !== ''} onPress={() => revPicker.current?.click()}>
                <Icon name="shield" size={16} />
                {busy === 'revocations' ? say('settings.licence.checking') : say('settings.licence.revocations')}
              </Button>
              {filePicker(picker, '.qlic,application/json', apply)}
              <Button variant="primary" isDisabled={busy !== ''} onPress={() => picker.current?.click()}>
                <Icon name="upload" size={16} />
                {/* Checked here, against a key built into the software; nothing is sent anywhere. */}
                {busy === 'licence'
                  ? say('settings.licence.checking')
                  : say(doc && !trial ? 'settings.licence.replace' : 'settings.licence.apply')}
              </Button>
            </>
          )
        }
      />
      {toast}
      {fault && <Notice className="os-gap">{fault}</Notice>}

      <SettingsCard title={say('settings.licence.status')}>
        <SettingRow
          label={
            <>
              <span className="lic-dot" data-tone={state.tone} aria-hidden="true" />
              {label}
            </>
          }
          status={
            says || status.fromServer ? (
              <>
                {says}
                {says && status.fromServer && ' '}
                {status.fromServer && say('admin.licence.fromServer')}
              </>
            ) : undefined
          }
        >
          {details.length > 0 && <Values items={details} />}
        </SettingRow>
        {own && (
          <SettingRow label={say('settings.licence.code')} description={say('settings.licence.codeLine')} control>
            <code className="lic-code">{status.installId}</code>
            <Button variant="ghost" onPress={() => void copyInstallId()}>
              <Icon name={copied ? 'check' : 'copy'} size={16} /> {say(copied ? 'settings.licence.copied' : 'settings.licence.copy')}
            </Button>
            <Button variant="ghost" isDisabled={busy !== ''} onPress={downloadRequest}>
              <Icon name="download" size={16} /> {say('settings.licence.request')}
            </Button>
          </SettingRow>
        )}
        {own && status.host && (
          <SettingRow label={say('settings.licence.machine')}>
            <span className="lic-fine">
              {say(status.host === 'standby' ? 'settings.licence.host.standby' : 'settings.licence.host.primary')}
            </span>
          </SettingRow>
        )}
        {own && status.revocations && (
          <SettingRow label={say('settings.licence.revocationList')}>
            <span className="lic-fine">
              {status.revocations.version} · {longDate(status.revocations.issuedAt)}
            </span>
          </SettingRow>
        )}
        {doc && !status.plan && !trial && (
          <SettingRow label={say(own ? 'settings.licence.file' : 'settings.licence.issued')}>
            <span className="lic-fine">
              {own ? (
                <>
                  {status.appliedAt && (
                    <>
                      Applied {longDate(status.appliedAt)}
                      {status.appliedBy ? ` by ${status.appliedBy}` : ''}.{' '}
                    </>
                  )}
                  Issued {longDate(doc.issuedAt)}.
                </>
              ) : (
                dated(doc.issuedAt, lang)
              )}
            </span>
          </SettingRow>
        )}
      </SettingsCard>

      {doc && !status.fromServer && <Usage status={status} say={say} />}
      {doc && <Modules status={status} say={say} />}
      {doc && !trial && <Dates status={status} say={say} lang={lang} />}
    </>
  )
}

function Dates({ status, say, lang }: { status: LicenceStatus; say: Say; lang: StaffLanguage }) {
  const doc = status.licence!
  const useLeft = doc.model === 'subscription' && doc.notAfter ? daysFromToday(doc.notAfter) : null
  const m = doc.maintenanceUntil
  const mLeft = m ? daysFromToday(m) : null
  const mTotal = m ? daysFromToday(m) - daysFromToday(doc.notBefore || doc.issuedAt) : 0
  const mUsed = mLeft !== null && mTotal > 0 ? Math.min(1, Math.max(0, (mTotal - mLeft) / mTotal)) : 1
  const mSoon = mLeft !== null && mLeft >= 0 && mLeft <= 60
  const mWarn = status.maintenanceExpired || (mLeft !== null && mLeft < 0) || mSoon

  return (
    <SettingsCard title={say('admin.licence.dates')}>
      <SettingRow
        label={say('admin.licence.rightToUse')}
        status={status.plan ? undefined : say(doc.model === 'perpetual' ? 'admin.licence.perpetual' : 'admin.licence.yearly')}
      >
        <span className="lic-when">
          {useLeft === null ? (
            <strong>{say('admin.licence.neverEnds')}</strong>
          ) : (
            <>
              <strong data-tone={useLeft < 0 ? 'warn' : undefined}>
                {say(useLeft < 0 ? 'admin.licence.ended' : 'admin.licence.until', { date: dated(doc.notAfter!, lang) })}
              </strong>
              <span>{inDays(useLeft, say)}</span>
            </>
          )}
        </span>
      </SettingRow>
      {!status.plan && (
        <SettingRow label={say('admin.licence.upgrades')} description={say('admin.licence.upgradesHint')}>
          <span className="lic-when">
            {m && mLeft !== null ? (
              <>
                <strong data-tone={mWarn ? 'warn' : undefined}>
                  {say(mLeft < 0 || status.maintenanceExpired ? 'admin.licence.ended' : 'admin.licence.until', { date: dated(m, lang) })}
                </strong>
                <span>{inDays(mLeft, say)}</span>
                <PeriodMeter share={mUsed} warn={mWarn} label={say('admin.licence.upgrades')} />
              </>
            ) : (
              <strong>{say('admin.licence.notCovered')}</strong>
            )}
          </span>
        </SettingRow>
      )}
    </SettingsCard>
  )
}

/** How much of the maintenance period has passed. */
function PeriodMeter({ share, warn, label }: { share: number; warn?: boolean; label: string }) {
  return (
    <Meter className="lic-meter" size="sm" aria-label={label} value={Math.round(Math.min(1, share) * 100)} color={warn ? 'warning' : 'default'}>
      <Meter.Track>
        <Meter.Fill />
      </Meter.Track>
    </Meter>
  )
}

const COUNTED: Record<string, StaffKey> = {
  staff: 'admin.licence.staff',
  branches: 'admin.licence.branches',
  tenants: 'admin.licence.tenants',
  devices: 'admin.licence.devices',
}

/** One limit as a card: the use large, the limit beside it, a meter under it. */
function UsageCard({ label, used, allowed, soft, say }: { label: string; used: number; allowed: number; soft?: boolean; say: Say }) {
  const state = meterState(used, allowed, soft)
  return (
    <li className="lic-card" data-state={state ?? 'none'}>
      <span className="lic-card__label">{label}</span>
      <span className="lic-card__value">
        <strong>{used}</strong>
        <span>{allowed > 0 ? `/ ${allowed}` : say('settings.licence.unlimitedWord')}</span>
      </span>
      {state ? (
        <Meter className="lic-meter" size="sm" aria-label={label} value={meterValue(used, allowed)} color={METER_COLOR[state]}>
          <Meter.Track>
            <Meter.Fill />
          </Meter.Track>
        </Meter>
      ) : (
        <span className="lic-card__track" aria-hidden="true" />
      )}
      {soft && <span className="lic-card__note">{say('settings.licence.soft')}</span>}
    </li>
  )
}

/** Counted by the server exactly as a create is refused, plus per-branch module seats. */
function Usage({ status, say }: { status: LicenceStatus; say: Say }) {
  const named = (name: string) => (COUNTED[name] ? say(COUNTED[name]!) : name)
  const seats = (status.modules ?? []).filter((m) => m.kind === 'branch' && m.bought && !m.every)
  return (
    <SettingsCard
      title={say('admin.licence.usage')}
      line={say(status.scope === 'organisation' ? 'settings.licence.organisation' : 'settings.licence.installation')}
    >
      <ul className="lic-cards">
        {(status.counted ?? []).map((c) => (
          <UsageCard key={c.name} label={named(c.name)} used={c.used} allowed={c.allowed} soft={c.soft} say={say} />
        ))}
        {seats.map((m) => (
          <UsageCard key={m.name} label={moduleName(m.name, m.label, say)} used={m.used ?? 0} allowed={m.seats ?? 0} say={say} />
        ))}
      </ul>
    </SettingsCard>
  )
}

/**
 * Every delivered module, bought or not: a list of what is on cannot answer "where
 * is Analytics?". A module this version has not built yet is named below instead.
 */
function Modules({ status, say }: { status: LicenceStatus; say: Say }) {
  const pending = status.pendingModules ?? []
  const unknown = status.unknownModules ?? []
  return (
    <SettingsCard title={say('admin.licence.modules')}>
      <div className="lic-mods">
        <ul className="lic-chips">
          {(status.modules ?? []).map((m) => (
            <li key={m.name}>
              <Chip
                size="md"
                variant={m.bought ? 'soft' : 'secondary'}
                color={m.bought ? 'success' : 'default'}
                data-off={!m.bought || undefined}
                aria-label={`${moduleName(m.name, m.label, say)}: ${say(m.bought ? 'admin.licence.bought' : 'admin.licence.notBought')}`}
              >
                {m.bought && <Icon name="check" size={14} />}
                {moduleName(m.name, m.label, say)}
              </Chip>
            </li>
          ))}
        </ul>
        {pending.length > 0 && <p className="lic-fine">{say('admin.licence.pending', { names: pending.join(', ') })}</p>}
        {/* A licence issued for a later version: those modules appear when this server is upgraded. */}
        {unknown.length > 0 && <p className="lic-fine">{say('admin.licence.unknown', { names: unknown.join(', ') })}</p>}
      </div>
    </SettingsCard>
  )
}
